import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { apiPublicPost } from "../../utils/api";
import { syncSessionProfile } from "../../utils/authService";
import { updateSocketToken } from "../../utils/socket";
import { toast } from "../../utils/toast";
import { ssoErrorMessage } from "../../utils/ssoErrorMessages";
import "./login.css";

// Same role→route map Login.jsx uses after a password login — kept as its own copy here rather
// than a shared utility, since Login.jsx is deliberately untouched by this feature (KAN-143: SSO
// is IdP-initiated only, nothing added to /login) and this is the only other caller.
function postLoginRedirect(res, navigate) {
  const role = res.role;

  if (res.requiresPasswordReset && (role === "admin" || role === "client")) {
    toast.error("Your password doesn't meet our security policy. Please set a stronger one to continue.");
    navigate("/update-password");
    return;
  }

  toast.success("Welcome! Authentication successful.");

  if (role === "admin") navigate("/admin/dashboard");
  else if (role === "client") navigate("/dashboard");
  else if (role === "analytics_viewer" || role === "ceo") navigate("/dashboard");
  else if (role === "cfo") navigate("/dashboard");
  else if (role === "employee") navigate("/dashboard");
  else if (role === "manager") navigate("/dashboard");
  else navigate("/role-accounts");
}

export default function SsoCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("working"); // working | error
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return; // StrictMode double-invoke guard — the code is single-use
    ranOnce.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const errorCode = params.get("error");

    if (errorCode) {
      toast.error(ssoErrorMessage(errorCode));
      navigate("/login", { replace: true });
      return;
    }

    if (!code) {
      setStatus("error");
      toast.error("Missing sign-in code.");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await apiPublicPost("/api/auth/sso/exchange", { code });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.token) {
          toast.error(data?.message || "Sign-in failed. Please try again.");
          navigate("/login", { replace: true });
          return;
        }
        syncSessionProfile(data);
        updateSocketToken(data.token);
        postLoginRedirect(data, navigate);
      } catch {
        setStatus("error");
        toast.error("Could not reach the server. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="login-container">
      <div className="login-card" style={{ textAlign: "center" }}>
        <Loader2 className="sso-callback-spin" size={28} />
        <p style={{ marginTop: 12, color: "#003366", fontWeight: 600 }}>
          {status === "working" ? "Completing sign-in…" : "Redirecting…"}
        </p>
      </div>
      <style>{`
        .sso-callback-spin { animation: sso-callback-spin 1s linear infinite; }
        @keyframes sso-callback-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
